import { Button, Col, Row, Tooltip } from "antd";
import { FunctionComponent } from "react";
import { SelectFormItem } from "./SelectFormItem";
import { DatePickerFormItem } from "./DatePickerFormItem";
import { InputFormItem } from "./InputFormItem";
import { DefaultOptionType } from "antd/es/select";
import { SearchOutlined } from "@ant-design/icons";
import { InputNumberFormItem } from "./InputNumberFormItem";

type InputFilterProps = {
    name: string;
    libelle: string;
    inputType: "Select" | "SelectTags" | "Date" | "InputText" | "InputNumber";
    selectOptions?: DefaultOptionType[];
    doSearch?: () => void;
};

/**
 * Composant de champ de filtre individuel.
 * Rendu en `labelCol` / `wrapperCol` en mode colonne (label au-dessus)
 * pour un meilleur rendu dans la grille responsive.
 */
const InputFilter: FunctionComponent<InputFilterProps> = ({
    name,
    libelle,
    inputType,
    selectOptions,
    doSearch,
    ...rest
}) => {
    /**
     * Props communes pour tous les FormItem dans la grille :
     * label au-dessus de l'input (layout vertical = pas de labelCol/wrapperCol).
     * La taille est normalisée à 100% de la colonne parente.
     */
    const sharedProps = {
        name,
        label: libelle,
        placeholder: libelle,
        style: { width: "100%" },
        ...rest,
    };

    switch (inputType) {
        case "Select":
            return (
                <SelectFormItem
                    {...sharedProps}
                    options={selectOptions}
                    key={name}
                    allowClear
                />
            );
        case "SelectTags":
            return (
                <SelectFormItem
                    {...sharedProps}
                    options={selectOptions}
                    key={name}
                    mode="tags"
                    allowClear
                />
            );
        case "Date":
            return (
                <DatePickerFormItem
                    onChange={doSearch}
                    {...sharedProps}
                    key={name}
                />
            );
        case "InputText":
            return (
                <InputFormItem
                    onPressEnter={doSearch}
                    {...sharedProps}
                    key={name}
                />
            );
        case "InputNumber":
            return (
                <InputNumberFormItem
                    onPressEnter={doSearch}
                    name={name}
                    label={libelle}
                    key={name}
                    {...rest}
                />
            );
        default:
            return <></>;
    }
};

export type InputSearchFieldDef = InputFilterProps & {
    tooltip?: string;
};

export type AdminSearchFilterProps = {
    doSearch: () => void;
    inputFilters: InputSearchFieldDef[];
};

/**
 * AdminSearchFilter — version grille responsive.
 *
 * Changements vs. version précédente :
 * - Layout en grille 3 colonnes (xl) → 2 (md) → 1 (xs)
 *   au lieu d'une liste verticale.
 *   Cela réduit la hauteur occupée de ~60 % pour 8 filtres.
 * - Suppression de la Card englobante : le conteneur est géré
 *   par le parent (InscriptionDesktopView), évitant la double imbrication.
 * - Bouton "Rechercher" aligné à droite dans la dernière ligne de grille.
 * - Les tooltips sont préservés sur les champs qui les ont définis.
 */
export const AdminSearchFilter: FunctionComponent<AdminSearchFilterProps> = ({
    inputFilters,
    doSearch,
}) => {
    const renderField = (inputFilter: InputSearchFieldDef) => {
        const { name, libelle, inputType, tooltip, selectOptions } = inputFilter;
        const field = (
            <InputFilter
                doSearch={doSearch}
                key={name + inputType}
                name={name}
                libelle={libelle}
                inputType={inputType}
                selectOptions={selectOptions}
            />
        );

        if (tooltip) {
            return (
                <Tooltip title={tooltip} color="geekblue" key={name}>
                    {field}
                </Tooltip>
            );
        }
        return <div key={name}>{field}</div>;
    };

    return (
        <>
            <Row gutter={[16, 4]}>
                {inputFilters.map((filter) => (
                    <Col key={filter.name} span={24}>
                        {renderField(filter)}
                    </Col>
                ))}
            </Row>

            {/* Bouton recherche aligné à droite */}
            <Row justify="end" style={{ marginTop: 8 }}>
                <Col>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={doSearch}
                        style={{ minWidth: 140 }}
                        {...({ testid: "rechercher" } as any)}
                    >
                        Rechercher
                    </Button>
                </Col>
            </Row>
        </>
    );
};